/**
 * getPayRunHoldAge — strict per-row hold-age resolver
 * ────────────────────────────────────────────────────
 * For a held PayRunView, computes the OLDEST triggering event across all
 * active hold reasons. Returns null under STRICT mode if any reason on
 * the row cannot be mapped to a timestamp.
 *
 * Why strict: a CFO uses aging to triage stuck money. A partial age
 * (some reasons contributing, others silently dropped) would systematically
 * UNDER-report severity. We'd rather show "—" than show a wrong number.
 *
 * Reason → timestamp source map (current model):
 *   • open_dispute                            → DisputeState.openedAt
 *   • holdback_required                       → HoldbackState first create entry
 *   • field_add_requires_accounting_approval  → oldest pending FieldAddSubmission.createdAt
 *   • critical_issue_open                     → oldest high-severity Callback.filedAt
 *   • warranty_issue_open                     → oldest high-severity Callback.filedAt
 *                                               (same source given current
 *                                                sumCallbackDeductions logic)
 *   • failed_qa                               → wrapRecord.timestamp
 *                                               (when outcome=completed_today
 *                                                but QA still not passed)
 *   • inspection_failed                       → NONE (no inspection model;
 *                                                also never set by any
 *                                                production caller)
 *
 * Pure: no side effects, no context access. Inject deps.
 */

import type { PayRunView, PayRunHoldReason } from "./getJobPayRun";
import type { Callback } from "@/data/contractor/networkExecution/callback";
import type { DisputeState } from "@/data/contractor/networkExecution/dispute/dispute";
import type { HoldbackState } from "@/data/contractor/networkExecution/holdback/deriveState";
import type { InstallerWrapRecord } from "@/data/installerWrapEngine";
import type { FieldAddSubmission } from "./fieldAdds";

export interface PayRunHoldAge {
  /** ISO timestamp of the oldest contributing event. */
  heldSince: string;
  /** Age in ms from `heldSince` to `now`. */
  ageMs: number;
  /** Which reason produced the oldest timestamp. */
  contributingReason: PayRunHoldReason;
}

export interface PayRunHoldAgeInput {
  view: PayRunView;
  wrapRecord?: InstallerWrapRecord;
  callbacks: Callback[];
  holdbacks: HoldbackState[];
  disputes: DisputeState[];
  /** Field-add submissions scoped to this job/party. */
  fieldAddSubmissions: FieldAddSubmission[];
  /** Override for deterministic tests. */
  now?: Date;
}

export function getPayRunHoldAge(input: PayRunHoldAgeInput): PayRunHoldAge | null {
  const reasons = input.view.holdReasons;
  if (reasons.length === 0) return null;

  const now = input.now ?? new Date();
  let oldestIso: string | null = null;
  let oldestReason: PayRunHoldReason | null = null;

  for (const reason of reasons) {
    const iso = resolveReasonTimestamp(reason, input);
    if (iso === null) return null; // STRICT: any unmapped reason → no age
    if (oldestIso === null || Date.parse(iso) < Date.parse(oldestIso)) {
      oldestIso = iso;
      oldestReason = reason;
    }
  }

  if (oldestIso === null || oldestReason === null) return null;
  const ageMs = Math.max(0, now.getTime() - Date.parse(oldestIso));
  return { heldSince: oldestIso, ageMs, contributingReason: oldestReason };
}

function resolveReasonTimestamp(
  reason: PayRunHoldReason,
  input: PayRunHoldAgeInput,
): string | null {
  switch (reason) {
    case "open_dispute": {
      const open = input.disputes.filter((d) => d.status === "open");
      const isos = open.map((d) => d.openedAt).filter(isIso);
      return isos.length ? oldest(isos) : null;
    }

    case "holdback_required": {
      const active = input.holdbacks.filter(
        (h) => (h.status === "active" || h.status === "paused") && h.netHeld > 0,
      );
      const isos: string[] = [];
      for (const h of active) {
        const create = h.entries.find((e) => e.action === "create");
        if (create && isIso(create.createdAt)) isos.push(create.createdAt);
      }
      return isos.length ? oldest(isos) : null;
    }

    case "failed_qa": {
      // Anchor: most recent completed_today wrap. That's the moment QA
      // sign-off became overdue. If no wrap or outcome ≠ completed_today,
      // the reason isn't actually traceable to a single event — strict null.
      if (!input.wrapRecord) return null;
      if (input.wrapRecord.outcome !== "completed_today") return null;
      return isIso(input.wrapRecord.timestamp) ? input.wrapRecord.timestamp : null;
    }

    case "critical_issue_open":
    case "warranty_issue_open": {
      // Both reasons currently anchor on high-severity callbacks
      // (see sumCallbackDeductions in getJobPayRun.ts).
      const high = input.callbacks.filter((c) => c.severity === "high");
      const isos = high.map((c) => c.filedAt).filter(isIso);
      return isos.length ? oldest(isos) : null;
    }

    case "field_add_requires_accounting_approval": {
      const pending = input.fieldAddSubmissions.filter((f) => f.status === "pending");
      const isos = pending.map((f) => f.createdAt).filter(isIso);
      return isos.length ? oldest(isos) : null;
    }

    case "inspection_failed":
      // No source-of-truth in the current model. Strict null.
      return null;

    default:
      return null;
  }
}

function isIso(s: unknown): s is string {
  return typeof s === "string" && !Number.isNaN(Date.parse(s));
}

function oldest(isos: string[]): string {
  return isos.reduce((a, b) => (Date.parse(a) <= Date.parse(b) ? a : b));
}

// ── Formatting helpers (UI-facing) ────────────────────────────────────────

export function formatHeldAge(ageMs: number): string {
  if (ageMs < 0) ageMs = 0;
  const mins = Math.floor(ageMs / 60_000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (days >= 1) return `${days}d ${hrs % 24}h`;
  if (hrs >= 1) return `${hrs}h ${mins % 60}m`;
  return `${mins}m`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export type HeldAgeTone = "fresh" | "warn" | "critical";

/** ≤ 2d fresh · ≤ 7d warn · > 7d critical. Conservative CFO thresholds. */
export function getHeldAgeTone(ageMs: number): HeldAgeTone {
  if (ageMs > 7 * DAY_MS) return "critical";
  if (ageMs > 2 * DAY_MS) return "warn";
  return "fresh";
}
